/// <amd-module name="Test/TestResultHistory/Actions/HistoryViewActionCreator" />
import { IViewContextData, ViewContext } from "Test/common-util/Common";
import * as TCMContracts from "Test/common-util/Contracts/Test";
import {
    HistoryViewActionsHub,
    IGroupedHistoryItem,
    IGroupedHistoryItemWithIndexAndNewResults,
} from "Test/TestResultHistory/Actions/HistoryViewActionHub";
import * as Utils_Date from "Test/TestResultHistory/DateUtils";
import { HistoryViewSource } from "Test/TestResultHistory/Source/HistoryViewSource";
import { IVssPageContext } from "VSS/Platform/Context";

export class HistoryViewActionsCreator {
    constructor(private _pageContext: IVssPageContext, private _actionsHub: HistoryViewActionsHub, private _source: HistoryViewSource) {
    }

    public fetchHistoryForBuildOrRelease(viewContext: IViewContextData, testCaseResult: TCMContracts.TestCaseResult, branch?: string, continuationToken?: string): void {
        let groupBy: TCMContracts.TestResultGroupBy = 0;
        if (viewContext.viewContext === ViewContext.Build) {
            groupBy = TCMContracts.TestResultGroupBy.Branch;
        } else if (viewContext.viewContext === ViewContext.Release) {
            groupBy = TCMContracts.TestResultGroupBy.Environment;
        }

        let testCase = testCaseResult.testCase && testCaseResult.testCase.id;
        let filter = {
            automatedTestName: testCaseResult.automatedTestName,
            testCaseId: !!testCase ? parseInt(testCase):0,
            buildDefinitionId: (viewContext.viewContext === ViewContext.Build) ? viewContext.data.mainData.definition.id : null,
            releaseEnvDefinitionId: (viewContext.viewContext === ViewContext.Release) ? viewContext.data.subData.environment.definitionEnvironmentId : null,
            groupBy: groupBy,
            branch: (branch != null) ? branch : null,
            continuationToken: continuationToken
        } as TCMContracts.TestHistoryQuery;

        this._source!.getTestRunById(this._pageContext, parseInt(testCaseResult.testRun.id)).then((testRun: TCMContracts.TestRun) => {
            filter.maxCompleteDate = testRun.completedDate;
            this._source!.getTestHistory(this._pageContext, filter).then((testHistory) => {
                this._actionsHub.currentBuildOrReleaseHistoryLoaded.invoke(testHistory);
                if (testHistory.continuationToken && testHistory.continuationToken !== "") {
                    this.fetchHistoryForBuildOrRelease(viewContext, testCaseResult, branch, testHistory.continuationToken);
                }
            })
                .catch((reason: any) => {
                    this._actionsHub.onError.invoke(reason);
                });
        })
            .catch((reason: any) => {
                this._actionsHub.onError.invoke(reason);
            });
    }

    public fetchHistoryForGroup(viewContext: IViewContextData, testCaseResult: TCMContracts.TestCaseResult, branch?: string, continuationToken?: string): void {
        this._actionsHub.clearBranchFilter.invoke(null);
        if (branch != null && continuationToken == null) {
            this._actionsHub.branchSelected.invoke(branch);
        }
        let groupBy: TCMContracts.TestResultGroupBy = 0;
        if (viewContext.viewContext === ViewContext.Build) {
            groupBy = TCMContracts.TestResultGroupBy.Branch;
        } else if (viewContext.viewContext === ViewContext.Release) {
            groupBy = TCMContracts.TestResultGroupBy.Environment;
        }

        let testCase = testCaseResult.testCase && testCaseResult.testCase.id;
        let filter = {
            automatedTestName: testCaseResult.automatedTestName,
            testCaseId: !!testCase ? parseInt(testCase):0,
            groupBy: groupBy,
            branch: (branch != null) ? branch : null,
            continuationToken: continuationToken
        } as TCMContracts.TestHistoryQuery;

        this._source.getTestRunById(this._pageContext, parseInt(testCaseResult.testRun.id)).then((testRun: TCMContracts.TestRun) => {
            filter.maxCompleteDate = testRun.completedDate;
            this._source.getTestHistory(this._pageContext, filter).then((testHistory) => {
                for (let i = 0; i < testHistory.resultsForGroup.length; ++i) {
                    if (viewContext.viewContext === ViewContext.Release && testHistory.resultsForGroup[i].groupByValue === viewContext.data.subData.environment.definitionEnvironmentId.toString()) {
                        testHistory.resultsForGroup.splice(i, 1);
                        break;
                    }
                }

                this._actionsHub.historyLoaded.invoke(testHistory);
                if (testHistory.continuationToken && testHistory.continuationToken !== "") {
                    this.fetchHistoryForGroup(viewContext, testCaseResult, branch, testHistory.continuationToken);
                }
            })
                .catch((reason: any) => {
                    this._actionsHub.onError.invoke(reason);
                });
        })
            .catch((reason: any) => {
                this._actionsHub.onError.invoke(reason);
            });
    }

    public regenerateHistory(): void {
        this._actionsHub.regenerateHistory.invoke(null);
    }

    public getNextOrPreviousResults(viewContext: IViewContextData, testCaseResult: TCMContracts.TestCaseResult, item: IGroupedHistoryItem,
        leftClicked: boolean, fetchMoreResults: boolean, branch?: string, index?: number): void {
        this._actionsHub.disableNextAndPrevButtons.invoke({ groupedHistoryItem: item, index: index });
        let groupedHistoryItemWithIndex: IGroupedHistoryItemWithIndexAndNewResults;
        if (!fetchMoreResults) {
            groupedHistoryItemWithIndex = {
                groupedHistoryItem: item,
                index: index
            };
            if (leftClicked) {
                this._actionsHub.previousButtonClicked.invoke(groupedHistoryItemWithIndex);
            } else {
                this._actionsHub.nextButtonClicked.invoke(groupedHistoryItemWithIndex);
            }
        } else {
            let groupBy: TCMContracts.TestResultGroupBy = 0;
            if (viewContext.viewContext === ViewContext.Build) {
                groupBy = TCMContracts.TestResultGroupBy.Branch;
            } else if (viewContext.viewContext === ViewContext.Release) {
                groupBy = TCMContracts.TestResultGroupBy.Environment;
            }

            let filter: TCMContracts.TestHistoryQuery;
            let testCase = testCaseResult.testCase && testCaseResult.testCase.id;
            // index null means request is for any particular branch or releaseEnvDefId
            if (index != null) {
                filter = {
                    automatedTestName: testCaseResult.automatedTestName,
                    testCaseId: !!testCase ? parseInt(testCase):0,
                    groupBy: groupBy,
                    branch: (viewContext.viewContext === ViewContext.Build) ? item.historyForGroup.groupByValue : branch,
                    releaseEnvDefinitionId: (viewContext.viewContext === ViewContext.Release) ? parseInt(item.historyForGroup.groupByValue) : null
                } as TCMContracts.TestHistoryQuery;
            } else {
                filter = {
                    automatedTestName: testCaseResult.automatedTestName,
                    testCaseId: !!testCase ? parseInt(testCase):0,
                    buildDefinitionId: (viewContext.viewContext === ViewContext.Build) ? parseInt(item.historyForGroup.groupByValue) : branch,
                    releaseEnvDefinitionId: (viewContext.viewContext === ViewContext.Release) ? parseInt(item.historyForGroup.groupByValue) : null,
                    groupBy: groupBy
                } as TCMContracts.TestHistoryQuery;
            }

            if (leftClicked) {
                filter.maxCompleteDate = Utils_Date.addDays(item.minDateWithData, -7);
            } else {
                filter.maxCompleteDate = Utils_Date.addDays(item.maxDateWithData, 7);
            }

            this._source.getTestHistory(this._pageContext, filter).then((testHistory) => {
                groupedHistoryItemWithIndex = {
                    groupedHistoryItem: item,
                    index: index,
                    newResults: testHistory
                };
                if (leftClicked) {
                    this._actionsHub.previousButtonClicked.invoke(groupedHistoryItemWithIndex);
                } else {
                    this._actionsHub.nextButtonClicked.invoke(groupedHistoryItemWithIndex);
                }
            })
                .catch((reason: any) => {
                    this._actionsHub.onError.invoke(reason);
                });
        }
    }

    public getBranches(testCaseResult: TCMContracts.TestCaseResult, projectName: string): void {
        let branchList: string[] = [];
        this._source.getBranchList(this._pageContext, projectName, testCaseResult).then((branches) => {
            for (let i = 0; i < branches.length; ++i) {
                branchList.push(branches[i].name);
            }
            this._actionsHub.branchesLoaded.invoke(branchList);
        }, (error) => {
            console.error(error);
            this._actionsHub.branchesLoaded.invoke([])
        });
    }

    public setMaxHistoryItemsToShow(setMaxHistoryItemsToShow: number): void {
        this._actionsHub.setMaxHistoryItemsToShow.invoke(setMaxHistoryItemsToShow);
    }
}
